import React, { useState, useEffect, useRef } from 'react';

function App() {
  const [videoInput, setVideoInput] = useState('');
  const [algorithm, setAlgorithm] = useState('regex');
  const [keywordFile, setKeywordFile] = useState(null);
  const [scanResults, setScanResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const eventSourceRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const closeEventSource = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      closeEventSource();
    };
  }, []);

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setScanResults([]);
    setError('');
    setMessage('');
    closeEventSource(); 

    let keywords = "";
    if (algorithm !== 'regex' && keywordFile) {
        try {
            const text = await keywordFile.text();
            // Membuat string keywords yang dipisahkan koma
            keywords = text.split(/\r?\n/).filter(line => line.trim() !== '').join(',');
        } catch (readError) {
            setError("Gagal membaca file kata kunci.");
            setLoading(false);
            return;
        }
    }

    const url = new URL(`${API_URL}/scan-stream`);
    url.searchParams.append('videoId', videoInput);
    url.searchParams.append('algorithm', algorithm);
    url.searchParams.append('keywords', keywords);

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      const newComments = JSON.parse(event.data);
      setScanResults(prevResults => [...prevResults, ...newComments]);
    };

    es.addEventListener('done', (event) => {
      const data = JSON.parse(event.data);
      setMessage(data.message || "Proses selesai.");
      setLoading(false);
      closeEventSource();
    });

    es.onerror = (err) => {
      setError('Koneksi ke server terputus atau terjadi error.');
      setLoading(false);
      closeEventSource();
    };
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen flex flex-col items-center p-4 sm:p-6">
      <div className="w-full max-w-2xl bg-gray-800 p-6 sm:p-8 rounded-xl shadow-2xl">
        <h1 className="text-3xl font-bold text-center mb-6">YouTube Comment Scanner</h1>
        <form onSubmit={handleScanSubmit} className="space-y-4">
          <div>
            <label htmlFor="video_input" className="block text-sm font-medium text-gray-300 mb-1">Link Video YouTube atau ID:</label>
            <input
              type="text"
              id="video_input"
              value={videoInput}
              onChange={(e) => setVideoInput(e.target.value)}
              required
              className="w-full bg-gray-700 border border-gray-600 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="algorithm" className="block text-sm font-medium text-gray-300 mb-1">Pilih Algoritma:</label>
            <select
              id="algorithm"
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="regex">Regex</option>
              <option value="kmp">KMP</option>
              <option value="bm">Boyer-Moore</option>
              <option value="rabin_karp">Rabin-Karp</option>
            </select>
          </div>
          {algorithm !== 'regex' && (
            <div>
              <label htmlFor="keyword_file" className="block text-sm font-medium text-gray-300 mb-1">Upload File Kata Kunci (.txt):</label>
              <input
                type="file"
                id="keyword_file"
                accept=".txt"
                onChange={(e) => setKeywordFile(e.target.files[0])}
                className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
              />
            </div>
          )}
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 font-bold py-2 px-4 rounded-md transition-colors duration-200 disabled:bg-gray-500 disabled:cursor-not-allowed">
            {loading ? 'Sedang Memindai...' : 'Telusuri Komentar'}
          </button>
        </form>
        <div className="mt-8">
          <h2 className="text-2xl font-bold border-b border-gray-700 pb-2 mb-4">Hasil Pencarian ({scanResults.length}):</h2>
          {loading && <p className="text-center animate-pulse">Memindai komentar, mohon tunggu...</p>}
          {error && <p className="text-center text-red-500 font-bold">{error}</p>}
          {message && !loading && <p className="text-center text-green-400">{message}</p>}
          <div className="space-y-4 max-h-96 overflow-y-auto mt-4">
            {scanResults.map((comment, index) => (
              <div key={index} className="bg-gray-700 p-4 rounded-lg">
                <p className="font-bold text-blue-400">{comment.author}</p>
                <p className="mt-1">{comment.text}</p>
                {comment.keyword && <p className="text-xs mt-2 text-green-400 font-mono">Kata Kunci: "{comment.keyword}"</p>}
              </div>
            ))}
            {!loading && scanResults.length === 0 && !error && <p className="text-center text-gray-500">Belum ada hasil.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
